import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import DropCursor from "@tiptap/extension-dropcursor";
import GapCursor from "@tiptap/extension-gapcursor";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { 
	Bold as BoldIcon, 
	Italic,
    Plus,
    Trash2,
    Underline as UnderlineIcon
} from "lucide-react";
import { useCallback } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";

interface SimplifiedSignatureEditorProps {
  content: string;
  onUpdate: (content: string) => void;
  onImageUpload?: (file: File) => Promise<string>;
  label?: string;
  disabled?: boolean;
  duneLogoUrl: string;
}

export function SimplifiedSignatureEditor({
  content,
  onUpdate,
  onImageUpload,
  label = "Signature",
  disabled = false,
  duneLogoUrl,
}: SimplifiedSignatureEditorProps) {
  const handleImageUpload = useCallback(async (file: File) => {
    if (!onImageUpload) return;
    
    try {
      const imageUrl = await onImageUpload(file);
      editor?.chain().focus().setImage({ src: imageUrl }).run();
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image:", error);
    }
  }, [onImageUpload]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        listItem: false,
        orderedList: false,
        bulletList: false,
      }),
      Underline,
      Image.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: "max-w-full h-auto",
        },
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: element => element.getAttribute('width'),
              renderHTML: attributes => {
                if (!attributes.width) {
                  return {};
                }
                return {
                  width: attributes.width,
                };
              },
            },
            height: {
              default: null,
              parseHTML: element => element.getAttribute('height'),
              renderHTML: attributes => {
                if (!attributes.height) {
                  return {};
                }
                return {
                  height: attributes.height,
                };
              },
            },
            alt: {
              default: null,
              parseHTML: element => element.getAttribute('alt'),
              renderHTML: attributes => {
                if (!attributes.alt) {
                  return {};
                }
                return {
                  alt: attributes.alt,
                };
              },
            },
            style: {
              default: null,
              parseHTML: element => element.getAttribute('style'),
              renderHTML: attributes => {
                if (!attributes.style) {
                  return {};
                }
                return {
                  style: attributes.style,
                };
              },
            },
          };
        },
      }),
      DropCursor,
      GapCursor,
      Placeholder.configure({
        placeholder: "Tapez votre signature ici...",
      }),
    ],
    content,
    shouldRerenderOnTransaction: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "focus:outline-none min-h-[8rem] max-w-none p-4 w-full",
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
      handlePaste: (_view, event, _slice) => {
        const items = Array.from(event.clipboardData?.items || []);
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageUpload(file);
              return true;
            }
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onUpdate(html);
    },
    editable: !disabled,
  });

  const insertDefaultTemplate = () => {
    const template = `
      <br />
      <p style="margin: 16px 0 8px 0;">
        <img src="${duneLogoUrl}" alt="DUNE" width="75" />
      </p>
      <p style="margin: 0;"><strong>Votre Nom</strong><br>Votre role | Votre sub role<br>+33 6 XX XX XX XX</p>
    `;

    editor?.chain().focus().setContent(template).run();
  };

  if (!editor) {
    return <div className="w-full min-h-[8rem] animate-pulse bg-muted/20 rounded" />;
  }

  return (
    <div className="w-full">
      {label && (
        <div className="text-sm font-medium mb-3">
          {label}
        </div>
      )}
      
      {!disabled && (
        <div className="flex items-center gap-1 mb-3 p-2 border rounded-md bg-background/50">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={cn("h-8 px-2", editor.isActive("bold") && "bg-muted")}
          >
            <BoldIcon className="w-4 h-4" />
          </Button>
          
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={cn("h-8 px-2", editor.isActive("italic") && "bg-muted")}
          >
            <Italic className="w-4 h-4" />
          </Button>
          
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={cn("h-8 px-2", editor.isActive("underline") && "bg-muted")}
          >
            <UnderlineIcon className="w-4 h-4" />
          </Button>
          
          <Separator orientation="vertical" className="h-6 mx-1" />
          
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={insertDefaultTemplate}
            className="h-8 px-3"
          >
            <Plus className="w-4 h-4 mr-1" />
            Template DUNE
          </Button>
          
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => editor.chain().focus().clearContent().run()}
            className="h-8 px-2 text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Effacer
          </Button>
        </div>
      )}
      
      <div className="relative w-full border rounded-md bg-white min-h-[8rem]">
        <EditorContent 
          editor={editor} 
          className="focus:outline-none min-h-[8rem] max-w-none p-4 w-full"
        />
      </div>
    </div>
  );
} 
